import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, KeyRound, Lock, ShieldAlert, Sparkles, X, BookmarkPlus } from 'lucide-react'
import { Modal, apiGet, adminMutate } from './primitives.js'
import { Checkbox, Field, TextInput } from './form.js'
import { Link } from '../router.js'
import { permMeta } from './catalog.js'
import './droits.css'

/**
 * FICHE D'UN COMPTE — « D'OÙ VIENNENT SES DROITS » (08/10/2026).
 *
 * Trois lectures, toutes calculées par le MOTEUR côté serveur
 * (GET /api/v1/admin/users/:id/rights), jamais recomposées ici :
 *  · MODULES : famille → module, accessible ou non, combien de droits, et
 *    d'où ils viennent (tel modèle, telle exception). Un module n'est
 *    « assigné » nulle part : il se DÉDUIT des droits effectifs ;
 *  · EXCEPTIONS : les cases par permission (Autorisé / Refusé) posées sur la
 *    personne, qui l'emportent sur ses modèles — et l'écran dit quand l'une
 *    d'elles ne change rien (un « Autorisé » sous un module fermé) ;
 *  · HISTORIQUE : modèles appliqués / retirés et exceptions, avec leur motif.
 *
 * Et deux gestes : changer les modèles appliqués (aperçu avant d'écrire :
 * droits gagnés / perdus, droits critiques → motif, refus de délégation
 * annoncé) et « Enregistrer comme modèle » (les droits EFFECTIFS du compte).
 */

/* ─────────── formes (garde de forme : une réponse inattendue n'invente rien) ─────────── */

export interface ModuleOverview {
  id: string; label: string; accessible: boolean; closed: boolean; closed_source: string | null
  allowed: number; total: number; via_templates: { key: string; name: string; count: number }[]
  exceptions_allow: number; exceptions_deny: number
}
export interface FamilyOverview { id: string; label: string; accessible: boolean; closed: boolean; closed_source: string | null; allowed: number; total: number; modules: ModuleOverview[] }
export interface ExceptionOverview {
  key: string; label: string; family_id: string | null; family_label: string | null; module_id: string | null; module_label: string | null
  is_gate: boolean; state: 'ALLOW' | 'DENY'; effective: boolean; reason: string | null; created_by: string | null; created_at: string
}
export interface AppliedTemplate { key: string; name: string; description: string | null; is_builtin: boolean; suggested: boolean; permissions: number }
export interface RightsOverview { templates: AppliedTemplate[]; exceptions: ExceptionOverview[]; families: FamilyOverview[] }

const isRec = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const num = (v: unknown): number => (typeof v === 'number' ? v : 0)
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])

export function shapeRights(v: unknown): RightsOverview | null {
  if (!isRec(v) || !Array.isArray(v['families'])) return null
  const modules = (m: unknown): ModuleOverview[] => arr(m).flatMap((x) => isRec(x) && typeof x['id'] === 'string' ? [{
    id: x['id'], label: str(x['label']) ?? x['id'], accessible: x['accessible'] === true, closed: x['closed'] === true,
    closed_source: str(x['closed_source']), allowed: num(x['allowed']), total: num(x['total']),
    via_templates: arr(x['via_templates']).flatMap((t) => isRec(t) && typeof t['key'] === 'string' ? [{ key: t['key'], name: str(t['name']) ?? t['key'], count: num(t['count']) }] : []),
    exceptions_allow: num(x['exceptions_allow']), exceptions_deny: num(x['exceptions_deny']),
  }] : [])
  return {
    templates: arr(v['templates']).flatMap((t) => isRec(t) && typeof t['key'] === 'string' ? [{
      key: t['key'], name: str(t['name']) ?? t['key'], description: str(t['description']), is_builtin: t['is_builtin'] === true,
      suggested: t['suggested'] === true, permissions: num(t['permissions']),
    }] : []),
    exceptions: arr(v['exceptions']).flatMap((e) => isRec(e) && typeof e['key'] === 'string' && (e['state'] === 'ALLOW' || e['state'] === 'DENY') ? [{
      key: e['key'], label: str(e['label']) ?? e['key'], family_id: str(e['family_id']), family_label: str(e['family_label']),
      module_id: str(e['module_id']), module_label: str(e['module_label']), is_gate: e['is_gate'] === true, state: e['state'],
      effective: e['effective'] !== false, reason: str(e['reason']), created_by: str(e['created_by']), created_at: str(e['created_at']) ?? '',
    }] : []),
    families: v['families'].flatMap((f) => isRec(f) && typeof f['id'] === 'string' ? [{
      id: f['id'], label: str(f['label']) ?? f['id'], accessible: f['accessible'] === true, closed: f['closed'] === true,
      closed_source: str(f['closed_source']), allowed: num(f['allowed']), total: num(f['total']), modules: modules(f['modules']),
    }] : []),
  }
}

const plural = (n: number, one: string, many: string): string => `${n} ${n > 1 ? many : one}`

/** Pourquoi un module ou une famille est fermé, dit du point de vue de la fiche. */
function fermeture(source: string | null): string {
  if (!source) return 'Fermé par un refus'
  if (source === 'Refusé directement') return 'Fermé par une exception posée sur cette personne'
  if (source.startsWith('Refusé : famille')) return 'Fermé avec toute sa famille'
  const parModele = /^Refusé par le rôle « (.+) »$/.exec(source)
  if (parModele) return `Fermé par le modèle « ${parModele[1]} »`
  return source
}
function fmtDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/* ═══════════ MODULES ═══════════ */

export function UserModulesBlock({ overview, error }: { overview: RightsOverview | null; error: string | null }) {
  if (error) return <p className="adm-note" role="alert"><strong>Modules indisponibles.</strong> {error}</p>
  if (!overview) return <p className="dr-hint">Calcul des modules en cours</p>
  const visibles = overview.families.filter((f) => f.allowed > 0 || f.closed || f.modules.some((m) => m.closed || m.exceptions_deny > 0))
  const sansAcces = overview.families.filter((f) => !visibles.includes(f))
  return (
    <>
      {visibles.length === 0 ? <p className="dr-hint">Ce compte n’ouvre aucun module : aucun modèle ni aucune exception ne lui accorde de droit.</p> : null}
      {visibles.map((f) => {
        const montres = f.modules.filter((m) => m.allowed > 0 || m.closed || m.exceptions_deny > 0)
        // « Sans droit » ne se dit que d'un module qui a des droits au catalogue :
        // un module qui n'en a aucun (Marché & prévisions, Instagram…) n'est pas
        // un droit refusé, même au Super Admin.
        const autres = f.modules.filter((m) => !montres.includes(m) && m.total > 0)
        const horsCatalogue = f.modules.filter((m) => !montres.includes(m) && m.total === 0)
        return (
          <section key={f.id} className="dr-family" aria-label={`Famille ${f.label}`}>
            <header className="dr-family__head">
              <span className="dr-family__name">{f.label}</span>
              {f.closed
                ? <span className="lc-pill lc-pill--error">Fermée</span>
                : f.accessible ? <span className="lc-pill lc-pill--ok">Accessible</span> : <span className="lc-pill lc-pill--idle">Aucun droit</span>}
              <span className="dr-family__count">{f.allowed} droit{f.allowed > 1 ? 's' : ''} sur {f.total}</span>
              {f.closed ? <span className="dr-module__closed">{fermeture(f.closed_source)}</span> : null}
            </header>
            {montres.map((m) => (
              <div key={m.id || 'general'} className="dr-module">
                <span className="dr-module__name">
                  {m.label}
                  <span className="dr-module__count">{m.allowed} / {m.total} droit{m.total > 1 ? 's' : ''}</span>
                </span>
                {m.closed
                  ? <span className="lc-pill lc-pill--error">Fermé</span>
                  : m.accessible ? <span className="lc-pill lc-pill--ok">Accessible</span> : <span className="lc-pill lc-pill--idle">Aucun droit</span>}
                <span className="dr-module__origin">
                  {m.closed
                    ? <span className="dr-module__closed">{fermeture(m.closed_source)}</span>
                    : null}
                  {!m.closed && m.via_templates.map((t) => <span key={t.key} className="dr-chip">via « {t.name} » · {t.count}</span>)}
                  {!m.closed && m.exceptions_allow > 0 ? <span className="dr-chip">+{m.exceptions_allow} par exception</span> : null}
                  {m.exceptions_deny > 0 ? <span className="dr-chip dr-chip--critical">−{m.exceptions_deny} retiré{m.exceptions_deny > 1 ? 's' : ''} par exception</span> : null}
                </span>
              </div>
            ))}
            {autres.length > 0 || horsCatalogue.length > 0 ? (
              <div className="dr-module">
                <span className="dr-module__name dr-hint" style={{ gridColumn: '1 / -1' }}>
                  {autres.length > 0 ? `Sans droit : ${autres.map((m) => m.label).join(', ')}` : null}
                  {autres.length > 0 && horsCatalogue.length > 0 ? ' · ' : null}
                  {horsCatalogue.length > 0 ? `Aucun droit propre au catalogue : ${horsCatalogue.map((m) => m.label).join(', ')}` : null}
                </span>
              </div>
            ) : null}
          </section>
        )
      })}
      {sansAcces.length > 0 ? (
        <p className="dr-noaccess">Aucun droit dans : {sansAcces.map((f) => f.label).join(', ')}.</p>
      ) : null}
    </>
  )
}

/* ═══════════ EXCEPTIONS ═══════════ */

export function UserExceptionsBlock({ overview, userId }: { overview: RightsOverview | null; userId: string }) {
  if (!overview) return null
  return (
    <>
      {overview.exceptions.length === 0 ? (
        <p className="dr-hint">Aucune exception : ce compte reçoit exactement ce que ses modèles accordent.</p>
      ) : (
        <div className="dr-lines">
          {overview.exceptions.map((e) => {
            const quoi = e.is_gate
              ? `${e.module_id ? 'Module' : 'Famille'} « ${e.label} » en entier`
              : e.label
            const ou = [e.family_label, e.is_gate ? null : e.module_label].filter(Boolean).join(' › ')
            return (
              <div key={e.key} className="dr-line">
                <span className="dr-line__main">
                  {quoi}
                  {ou ? <span className="dr-line__where">{ou}</span> : null}
                </span>
                {e.state === 'ALLOW'
                  ? <span className="lc-pill lc-pill--ok">Autorisé</span>
                  : <span className="lc-pill lc-pill--error">Refusé</span>}
                {!e.effective ? <span className="lc-pill lc-pill--warning">Sans effet : le module ou la famille est fermé</span> : null}
                <span className="dr-line__meta">{e.created_by ? `${e.created_by}, ` : ''}{fmtDate(e.created_at)}</span>
                {e.reason ? <span className="dr-line__reason">Motif : « {e.reason} »</span> : null}
              </div>
            )
          })}
        </div>
      )}
      <div className="dr-block__actions">
        <Link href={`/settings/users/${userId}/permissions`} className="lc-btn lc-btn--ghost">
          <KeyRound size={14} /> {overview.exceptions.length > 0 ? 'Modifier les exceptions' : 'Poser une exception'}
        </Link>
      </div>
    </>
  )
}

/* ═══════════ HISTORIQUE ═══════════ */

interface HistoryEntry {
  id: string; createdAt: string; actorName: string | null; permissionKey: string | null; family: string | null; module: string | null
  stateBefore: string | null; stateAfter: string | null; reason: string | null; templateKey: string | null; templateName: string | null
}
function shapeHistory(v: unknown): HistoryEntry[] | null {
  if (!isRec(v) || !Array.isArray(v['history'])) return null
  return v['history'].flatMap((e) => isRec(e) && typeof e['id'] === 'string' ? [{
    id: e['id'], createdAt: str(e['createdAt']) ?? '', actorName: str(e['actorName']), permissionKey: str(e['permissionKey']),
    family: str(e['family']), module: str(e['module']), stateBefore: str(e['stateBefore']), stateAfter: str(e['stateAfter']),
    reason: str(e['reason']), templateKey: str(e['templateKey']), templateName: str(e['templateName']),
  }] : [])
}
const ETAT: Record<string, string> = { ALLOW: 'Autorisé', DENY: 'Refusé', INHERITED: 'Hérité des modèles' }
const etat = (s: string | null): string => (s ? ETAT[s] ?? (s.startsWith('ALLOW') ? 'Autorisé' : s) : 'Hérité des modèles')

export function UserRightsHistory({ userId, overview, reloadKey }: { userId: string; overview: RightsOverview | null; reloadKey: number }) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    apiGet<unknown>(`/api/v1/admin/permissions/history?targetType=USER&targetId=${encodeURIComponent(userId)}`).then((r) => {
      if (!alive) return
      const h = shapeHistory(r)
      if (h) { setEntries(h); setError(null) }
      else { setEntries([]); setError(isRec(r) && r['error'] === 'FORBIDDEN' ? 'Réservé aux titulaires du droit « Consulter le journal d’audit ».' : 'Réponse inattendue du serveur.') }
    }).catch(() => { if (alive) { setEntries([]); setError('Le serveur n’a pas répondu.') } })
    return () => { alive = false }
  }, [userId, reloadKey])

  const famLabel = useMemo(() => new Map((overview?.families ?? []).map((f) => [f.id, f.label])), [overview])
  const modLabel = useMemo(() => new Map((overview?.families ?? []).flatMap((f) => f.modules.map((m) => [`${f.id}/${m.id}`, m.label] as const))), [overview])
  const phrase = (e: HistoryEntry): string => {
    if (e.templateKey) {
      const nom = e.templateName ?? e.templateKey
      if (e.stateAfter === 'APPLIQUE') return `Modèle « ${nom} » appliqué`
      if (e.stateAfter === 'NON_APPLIQUE') return `Modèle « ${nom} » retiré`
      return `Modèle « ${nom} » supprimé`
    }
    if (e.permissionKey) return `« ${permMeta(e.permissionKey).label} » : ${etat(e.stateBefore)} → ${etat(e.stateAfter)}`
    const cible = e.module
      ? `au module « ${modLabel.get(`${e.family}/${e.module}`) ?? e.module} »`
      : `à la famille « ${famLabel.get(e.family ?? '') ?? e.family ?? '—'} »`
    return `Accès ${cible} : ${etat(e.stateBefore)} → ${etat(e.stateAfter)}`
  }

  if (error) return <p className="dr-hint">Historique indisponible : {error}</p>
  if (!entries) return <p className="dr-hint">Chargement de l’historique en cours</p>
  if (entries.length === 0) return <p className="dr-hint">Aucun changement de droits enregistré pour ce compte.</p>
  const shown = entries.slice(0, 15)
  return (
    <div className="dr-lines">
      {shown.map((e) => (
        <div key={e.id} className="dr-line">
          <span className="dr-line__main">{phrase(e)}</span>
          <span className="dr-line__meta">{e.actorName ?? 'Système'}, {fmtDate(e.createdAt)}</span>
          {e.reason ? <span className="dr-line__reason">Motif : « {e.reason} »</span> : null}
        </div>
      ))}
      {entries.length > shown.length ? <p className="dr-hint" style={{ paddingTop: 'var(--sp-2)' }}>{plural(entries.length - shown.length, 'changement plus ancien', 'changements plus anciens')} dans l’audit.</p> : null}
    </div>
  )
}

/* ═══════════ CHANGER LES MODÈLES — aperçu, motif, refus annoncé ═══════════ */

interface TemplateChoice { key: string; name: string; description: string | null; is_builtin: boolean; suggested: boolean; protected: boolean; permissions: number }
interface RightRef { key: string; label: string; critical: boolean }
interface Plan {
  added: { key: string; name: string }[]; removed: { key: string; name: string }[]
  gained: RightRef[]; lost: RightRef[]; criticalCount: number; reasonRequired: boolean
  refusal: { code: string; message: string } | null
}
function shapePlan(v: unknown): Plan | null {
  if (!isRec(v) || !Array.isArray(v['gained'])) return null
  const refs = (x: unknown) => arr(x).flatMap((t) => isRec(t) && typeof t['key'] === 'string' ? [{ key: t['key'], name: str(t['name']) ?? t['key'] }] : [])
  const rights = (x: unknown): RightRef[] => arr(x).flatMap((t) => isRec(t) && typeof t['key'] === 'string' ? [{ key: t['key'], label: str(t['label']) ?? t['key'], critical: t['critical'] === true }] : [])
  const r = v['refusal']
  return {
    added: refs(v['added']), removed: refs(v['removed']), gained: rights(v['gained']), lost: rights(v['lost']),
    criticalCount: num(v['criticalCount']), reasonRequired: v['reasonRequired'] === true,
    refusal: isRec(r) && typeof r['message'] === 'string' ? { code: str(r['code']) ?? '', message: r['message'] } : null,
  }
}

export function ApplyTemplatesModal(props: {
  open: boolean
  userId: string
  userName: string
  current: string[]
  onClose: () => void
  onApplied: (message: string) => void
}) {
  const [choices, setChoices] = useState<TemplateChoice[] | null>(null)
  const [sel, setSel] = useState<Set<string>>(new Set(props.current))
  const [plan, setPlan] = useState<Plan | null>(null)
  const [planError, setPlanError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seq = useRef(0)

  useEffect(() => {
    if (!props.open) return
    setSel(new Set(props.current)); setReason(''); setError(null); setPlan(null)
    apiGet<unknown>('/api/v1/admin/roles').then((j) => {
      setChoices(isRec(j) && Array.isArray(j['roles']) ? j['roles'].flatMap((t) => isRec(t) && typeof t['key'] === 'string' && typeof t['name'] === 'string' ? [{
        key: t['key'], name: t['name'], description: str(t['description']), is_builtin: t['is_builtin'] === true,
        suggested: t['suggested'] === true, protected: t['protected'] === true || t['key'] === 'super_admin', permissions: num(t['permissions']),
      }] : []) : [])
    }).catch(() => setChoices([]))
  }, [props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  const selKey = [...sel].sort().join('|')
  const changed = selKey !== [...props.current].sort().join('|')
  useEffect(() => {
    if (!props.open || !changed) { setPlan(null); setPlanError(null); return }
    const n = ++seq.current
    const t = setTimeout(() => {
      void adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/admin/users/${props.userId}/roles/preview`, { roles: [...sel] })
        .then((r) => {
          if (n !== seq.current) return
          const p = r.status === 200 ? shapePlan(r.data) : null
          if (p) { setPlan(p); setPlanError(null) } else { setPlan(null); setPlanError(r.data.message ?? 'Aperçu indisponible.') }
        })
    }, 250)
    return () => clearTimeout(t)
  }, [selKey, props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  const reasonMissing = (plan?.reasonRequired ?? false) && !reason.trim()
  const canApply = changed && plan !== null && plan.refusal === null && !reasonMissing && !busy

  const appliquer = async () => {
    setBusy(true); setError(null)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/admin/users/${props.userId}/roles`, { roles: [...sel], reason: reason.trim() || null })
    setBusy(false)
    if (r.status === 200) {
      const p = plan
      props.onApplied(p ? `Modèles enregistrés : ${plural(p.gained.length, 'droit gagné', 'droits gagnés')}, ${plural(p.lost.length, 'perdu', 'perdus')}.` : 'Modèles enregistrés.')
    } else setError(r.data.message ?? 'Les modèles n’ont pas été modifiés. Rien n’a changé.')
  }

  const groupes: { titre: string; list: TemplateChoice[] }[] = [
    { titre: 'Personnalisés', list: (choices ?? []).filter((c) => !c.is_builtin && !c.suggested) },
    { titre: 'Proposés', list: (choices ?? []).filter((c) => !c.is_builtin && c.suggested) },
    { titre: 'Intégrés', list: (choices ?? []).filter((c) => c.is_builtin) },
  ]
  const toggle = (k: string, on: boolean) => setSel((prev) => { const s = new Set(prev); if (on) s.add(k); else s.delete(k); return s })

  return (
    <Modal open={props.open} onClose={props.onClose} ariaLabel="Modèles appliqués" width={720} owning>
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-blue)' }}><KeyRound size={20} /></span>
        <div>
          <div className="lc-modal__title">Modèles de {props.userName}</div>
          <div className="pm-modal__sub">Cochez les modèles à appliquer : le compte reçoit tout ce qu’ils accordent. Ses exceptions restent en place.</div>
        </div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={props.onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body">
        {choices === null ? <p className="dr-hint">Chargement des modèles en cours</p> : null}
        {groupes.map((g) => g.list.length === 0 ? null : (
          <div key={g.titre}>
            <div className="dr-group-title">{g.titre}</div>
            <div className="dr-choices">
              {g.list.map((c) => (
                <div key={c.key} className={`dr-choice${sel.has(c.key) ? ' dr-choice--on' : ''}`}>
                  <Checkbox checked={sel.has(c.key)} onChange={(on) => toggle(c.key, on)} label={
                    <span className="dr-choice__text">
                      <span className="dr-choice__name">
                        {c.name}{' '}
                        {c.protected ? <Lock size={12} aria-label="protégé" /> : c.suggested ? <Sparkles size={12} aria-label="proposé" /> : null}
                      </span>
                      {c.description ? <span className="dr-choice__desc">{c.description}</span> : null}
                      <span className="dr-choice__meta">{c.protected ? 'Tout le catalogue' : plural(c.permissions, 'droit', 'droits')}</span>
                    </span>
                  } />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="dr-impact" aria-live="polite">
          {!changed ? <p className="dr-hint">Aucun changement pour l’instant.</p> : null}
          {changed && !plan && !planError ? <p className="dr-hint">Calcul de l’impact en cours</p> : null}
          {planError ? <p className="dr-refusal" role="alert"><AlertTriangle size={14} aria-hidden="true" /> <span>{planError}</span></p> : null}
          {plan ? (
            <>
              <p className="dr-impact__line">
                <strong>+{plan.gained.length}</strong> droit{plan.gained.length > 1 ? 's' : ''} gagné{plan.gained.length > 1 ? 's' : ''}
                {' · '}<strong>−{plan.lost.length}</strong> perdu{plan.lost.length > 1 ? 's' : ''}
              </p>
              {plan.gained.length > 0 ? (
                <div className="dr-chips">
                  {plan.gained.map((g) => <span key={g.key} className={`dr-chip${g.critical ? ' dr-chip--critical' : ''}`}>{g.critical ? <ShieldAlert size={12} aria-label="critique" /> : null}{g.label}</span>)}
                </div>
              ) : null}
              {plan.lost.length > 0 ? (
                <div className="dr-chips">
                  {plan.lost.map((g) => <span key={g.key} className="dr-chip dr-chip--lost">{g.label}</span>)}
                </div>
              ) : null}
              {plan.refusal ? (
                <p className="dr-refusal" role="alert"><Lock size={14} aria-hidden="true" /> <span>{plan.refusal.message}</span></p>
              ) : null}
              {plan.reasonRequired && !plan.refusal ? (
                <>
                  <p className="dr-critical-note"><ShieldAlert size={14} aria-hidden="true" /> {plural(plan.criticalCount, 'droit critique', 'droits critiques')} : un motif est obligatoire, et une ré-authentification vous sera demandée.</p>
                  <Field label="Motif" helper="Conservé dans l’historique des droits du compte.">
                    <TextInput value={reason} onChange={setReason} maxLength={500} placeholder="Pourquoi ce changement ?" ariaLabel="Motif du changement de modèles" />
                  </Field>
                </>
              ) : null}
            </>
          ) : null}
          {error ? <p className="dr-refusal" role="alert"><AlertTriangle size={14} aria-hidden="true" /> <span>{error}</span></p> : null}
        </div>
      </div>
      <footer className="pm-modal__foot dr-foot">
        {/* Le résumé reste sous les yeux même quand la liste défile. */}
        <span className="dr-foot-summary" aria-live="polite">
          {!changed ? 'Aucun changement' : !plan ? (planError ? <span className="dr-foot-summary__ko">Aperçu indisponible</span> : 'Calcul de l’impact en cours') : (
            <>
              <span><strong>+{plan.gained.length}</strong> droit{plan.gained.length > 1 ? 's' : ''} · <strong>−{plan.lost.length}</strong></span>
              {plan.refusal ? <span className="dr-foot-summary__ko">Refusé : voir le détail</span>
                : plan.reasonRequired ? <span className={reasonMissing ? 'dr-foot-summary__warn' : undefined}>{reasonMissing ? 'Motif à indiquer' : 'Motif indiqué'}</span> : null}
            </>
          )}
        </span>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onClose} disabled={busy}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" onClick={() => void appliquer()} disabled={!canApply}>
          {busy ? 'Enregistrement en cours' : 'Appliquer les modèles'}
        </button>
      </footer>
    </Modal>
  )
}

/* ═══════════ ENREGISTRER COMME MODÈLE ═══════════ */

export function SaveAsTemplateModal(props: {
  open: boolean
  userId: string
  userName: string
  rightsCount: number | null
  onClose: () => void
  onSaved: (key: string, name: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!props.open) return
    setName(`Profil de ${props.userName}`.slice(0, 80)); setDescription(''); setError(null)
  }, [props.open, props.userName])

  const enregistrer = async () => {
    setBusy(true); setError(null)
    const body: Record<string, unknown> = { name: name.trim(), from_user: props.userId }
    if (description.trim()) body['description'] = description.trim()
    const r = await adminMutate<{ key?: string; name?: string; error?: string; message?: string }>('POST', '/api/v1/admin/roles', body)
    setBusy(false)
    if (r.status === 201 && r.data.key) props.onSaved(r.data.key, r.data.name ?? name.trim())
    else setError(r.data.message ?? 'Le modèle n’a pas été créé. Rien n’a changé.')
  }

  return (
    <Modal open={props.open} onClose={props.onClose} ariaLabel="Enregistrer comme modèle" width={560} owning>
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-emerald)' }}><BookmarkPlus size={20} /></span>
        <div>
          <div className="lc-modal__title">Enregistrer comme modèle</div>
          <div className="pm-modal__sub">
            Le nouveau modèle reprend ce que {props.userName} peut faire aujourd’hui
            {props.rightsCount !== null ? ` (${plural(props.rightsCount, 'droit', 'droits')})` : ''} : modèles et exceptions combinés, refus déduits.
          </div>
        </div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={props.onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body">
        <Field label="Nom du modèle" error={name.trim().length < 2 ? 'Le nom fait au moins 2 caractères.' : null}>
          <TextInput value={name} onChange={setName} maxLength={80} ariaLabel="Nom du modèle" />
        </Field>
        <Field label="Description" helper="À quoi sert ce modèle, pour qui.">
          <TextInput value={description} onChange={setDescription} maxLength={300} ariaLabel="Description du modèle" />
        </Field>
        <p className="dr-hint">Le compte n’est pas modifié, et le modèle n’est appliqué à personne : vous l’appliquerez ensuite à qui vous voulez.</p>
        {error ? <p className="dr-refusal" role="alert"><AlertTriangle size={14} aria-hidden="true" /> <span>{error}</span></p> : null}
      </div>
      <footer className="pm-modal__foot">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onClose} disabled={busy}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" onClick={() => void enregistrer()} disabled={busy || name.trim().length < 2}>
          {busy ? 'Création en cours' : 'Créer le modèle'}
        </button>
      </footer>
    </Modal>
  )
}
